import { parse, isValid, addYears } from 'date-fns';
import { enUS } from 'date-fns/locale';

import type { EditorState } from '@atlaskit/editor-prosemirror/state';

import { getPluginState } from '../../plugin-factory';

export class LocaleManager {
	public static readonly LOCALES: Record<string, Locale> = {
		enUS,
	};

	public static getSelectedLocale(state: EditorState): string {
		const pluginState = getPluginState(state);
		return pluginState?.locale || 'en-US';
	}
}

export class DateParser {
	private static readonly MIN_DATE_LENGTH: number = 3;
	private static readonly MAX_DATE_LENGTH: number = 20;
	private static readonly DATE_FORMATS_BY_LOCALE: Record<string, string[]> = {
		'en-GB': [
			// --- Numeric formats with 4-digit year ---
			'd/M/yyyy', // 1/1/2025
			'd-M-yyyy', // 1-1-2025
			'd.M.yyyy', // 1.1.2025
			'dd/MM/yyyy', // 01/01/2025
			'dd-MM-yyyy', // 01-01-2025
			'dd.MM.yyyy', // 01.01.2025

			// --- Numeric formats with 2-digit year ---
			'd/M/yy', // 1/1/25
			'd-M-yy', // 1-1-25
			'd.M.yy', // 1.1.25
			'dd/MM/yy', // 01/01/25
			'dd-MM-yy', // 01-01-25
			'dd.MM.yy', // 01.01.25

			// --- Month name formats with 4-digit year ---
			'd MMM yyyy', // 1 Jan 2025
			'd MMMM yyyy', // 1 January 2025
			'dd MMM yyyy', // 01 Jan 2025
			'dd MMMM yyyy', // 01 January 2025

			// --- Month name formats with 2-digit year (if needed) ---
			'd MMM yy', // 1 Jan 25
			'd MMMM yy', // 1 January 25
			'dd MMM yy', // 01 Jan 25
			'dd MMMM yy', // 01 January 25

			// --- Without year (defaults to current year) ---
			'd MMMM', // 1 January
			'd MMM', // 1 Jan
			'do MMMM', // 1st January
			'do MMM', // 1st Jan
			"do 'of' MMMM", // 1st of January
			"do 'of' MMM", // 1st of Jan
			'MMMM d', // January 1
			'MMMM d', // Jan 1

			// --- Additional common formats ---
			'yyyy-MM-dd', // ISO style: 2025-01-01
			"MMMM d', 'yyyy", // January 1, 2025 (with comma)
			'MMMM d yyyy', // January 1 2025 (without comma)
			"MMM d', 'yyyy", // Jan 1, 2025 (with comma)
			'MMM d yyyy', // Jan 1 2025 (without comma)
		],
	};

	private static isPotentialDateString(input: string): boolean {
		return (
			/\d/u.test(input) &&
			input.length >= this.MIN_DATE_LENGTH &&
			input.length <= this.MAX_DATE_LENGTH
		);
	}

	private static normalizeYear(date: Date): Date {
		const yearStr = date.getFullYear().toString();
		return yearStr.length < 4 ? addYears(date, 2000) : date;
	}

	private static containsAmbiguousMonth(input: string): boolean {
		const matches = input.match(/[A-Za-z]+/gu);
		if (!matches) {
			return false;
		}
		const longestTokenLength = matches.reduce((max, token) => Math.max(max, token.length), 0);
		return longestTokenLength <= 2;
	}

	public static parse(input: string, locale: string): Date | null {
		if (!this.isPotentialDateString(input)) {
			return null;
		}
		if (this.containsAmbiguousMonth(input)) {
			return null;
		}
		const dateFnsLocale = LocaleManager.LOCALES[locale] || enUS;
		const formats = this.DATE_FORMATS_BY_LOCALE[locale] || [];
		for (const format of formats) {
			const parsed = parse(input, format, new Date(), { locale: dateFnsLocale });
			if (isValid(parsed)) {
				return this.normalizeYear(parsed);
			}
		}
		return null;
	}
}

export class DateUtils {
	public static getDateOnlyUtcTimestamp(date: Date): number {
		return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
	}
}
